import { defineStore } from "pinia"

export const useMessagesStore = defineStore("messages", {
  state: () => ({
    conversations: [],
    activeConversation: null,
    messages: {},
    unreadCount: 0,
    isLoading: false,
    error: null,
  }),

  getters: {
    conversationsList: (state) => {
      return [...state.conversations].sort((a, b) => new Date(b.last_message_at || b.created_at) - new Date(a.last_message_at || a.created_at))
    },

    unreadConversations: (state) => {
      return state.conversations.filter((c) => c.unread_count > 0)
    },

    getConversationMessages: (state) => (conversationId) => {
      return state.messages[conversationId] || []
    },

    totalUnreadCount: (state) => {
      return state.conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0)
    },

    getConversationById: (state) => (conversationId) => {
      return state.conversations.find((c) => c.id === conversationId)
    },
  },

  actions: {
    /**
     * Fetch all conversations for the authenticated user
     */
    async fetchConversations() {
      this.isLoading = true
      this.error = null

      try {
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.get("/api/v1/notifications/messages/conversations")

        this.conversations = response.data.data
        this.unreadCount = this.totalUnreadCount

        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to fetch conversations"
        return { success: false, error: this.error }
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Create a new conversation
     */
    async createConversation(participantIds, initialMessage = null) {
      this.isLoading = true
      this.error = null

      try {
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.post("/api/v1/notifications/messages/conversations", {
          participant_ids: participantIds,
          initial_message: initialMessage,
        })

        this.conversations.unshift(response.data.data)

        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to create conversation"
        return { success: false, error: this.error }
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Fetch messages for a specific conversation
     */
    async fetchMessages(conversationId) {
      this.isLoading = true
      this.error = null

      try {
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.get(`/api/v1/notifications/messages/conversations/${conversationId}/messages`)

        this.messages[conversationId] = response.data.data
        this.activeConversation = conversationId

        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to fetch messages"
        return { success: false, error: this.error }
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Send a message in a conversation
     */
    async sendMessage(conversationId, message, attachments = []) {
      this.error = null

      try {
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.post(`/api/v1/notifications/messages/conversations/${conversationId}/messages`, {
          message,
          attachments,
        })

        // Add message to local state
        if (!this.messages[conversationId]) {
          this.messages[conversationId] = []
        }
        this.messages[conversationId].push(response.data.data)

        // Update conversation's last message
        const conversation = this.conversations.find((c) => c.id === conversationId)
        if (conversation) {
          conversation.last_message = message
          conversation.last_message_at = new Date().toISOString()
        }

        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to send message"
        return { success: false, error: this.error }
      }
    },

    /**
     * Mark message as read
     */
    async markAsRead(messageId, conversationId) {
      try {
        // Using this.$axios injected by pinia plugin
        await this.$axios.post(`/api/v1/notifications/messages/messages/${messageId}/read`)

        // Update unread count
        const conversation = this.conversations.find((c) => c.id === conversationId)
        if (conversation && conversation.unread_count > 0) {
          conversation.unread_count--
          this.unreadCount = this.totalUnreadCount
        }

        return { success: true }
      } catch (error) {
        return { success: false, error: error.response?.data?.message }
      }
    },

    /**
     * Mark all messages in conversation as read
     */
    async markConversationAsRead(conversationId) {
      const conversation = this.conversations.find((c) => c.id === conversationId)
      if (!conversation || conversation.unread_count === 0) return

      const messages = this.messages[conversationId] || []
      const unreadMessages = messages.filter((m) => !m.is_read && m.sender_id !== conversation.current_user_id)

      for (const message of unreadMessages) {
        await this.markAsRead(message.id, conversationId)
      }
    },

    /**
     * Handle incoming WebSocket message
     */
    handleNewMessage(message) {
      const { conversationId, data } = message

      // Add message to conversation
      if (!this.messages[conversationId]) {
        this.messages[conversationId] = []
      }
      this.messages[conversationId].push(data)

      // Update conversation
      const conversation = this.conversations.find((c) => c.id === conversationId)
      if (conversation) {
        conversation.last_message = data.message
        conversation.last_message_at = data.created_at
        if (!data.is_own_message) {
          conversation.unread_count = (conversation.unread_count || 0) + 1
          this.unreadCount = this.totalUnreadCount
        }
      }
    },

    /**
     * Set active conversation
     */
    setActiveConversation(conversationId) {
      this.activeConversation = conversationId
    },

    /**
     * Clear error
     */
    clearError() {
      this.error = null
    },

    /**
     * Reset store
     */
    reset() {
      this.conversations = []
      this.activeConversation = null
      this.messages = {}
      this.unreadCount = 0
      this.isLoading = false
      this.error = null
    },
  },
})
